import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { identifyUser } from '../lib/clarity';

// Exported so the demo route can supply a fabricated persona in place of the real session
// (src/demo/DemoProvider.jsx). Nothing else should provide this context.
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession]   = useState(undefined); // undefined = loading
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    // If Supabase not configured, skip auth entirely
    if (!supabase) {
      setSession(null);
      setLoading(false);
      return;
    }

    // Get the current session on mount
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
      // Tie Clarity recordings to the signed-in user (no-ops off production).
      if (session?.user) identifyUser(session.user);
    });

    // Listen for auth state changes (login, logout, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session?.user) identifyUser(session.user);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function signOut() {
    if (supabase) await supabase.auth.signOut();
    // Clear all per-user localStorage data on sign out
    localStorage.removeItem('repbuddy_chats');
    localStorage.removeItem('repbuddy_seller_profile');
    localStorage.removeItem('repbuddy_active_chat');
    localStorage.removeItem('repbuddy_page_mode');
    localStorage.removeItem('repbuddy_user_id');
    localStorage.removeItem('repbuddy_access_status');
    setSession(null);
  }

  // Helper: returns the current JWT for use in API calls
  function getToken() {
    return session?.access_token || null;
  }

  return (
    <AuthContext.Provider value={{
      user:    session?.user  || null,
      session,
      loading,
      signOut,
      getToken,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
